import {useParams} from 'react-router-dom'
import * as Yup from 'yup'
import {FC, useEffect, useState} from 'react'
import {appsAPI} from '../../../../../../services/AppsService'
import {KTSVG} from '../../../../../../template/helpers'
import {useFormik} from 'formik'
import {IAppsModel} from '../../../../../../types/apps/AppsModel'
import {IAppAmoCRMLine} from '../../../../../../template/partials/modals/create-app/IAppModels'
import clsx from 'clsx'
import {IAmoCRMUserModel} from '../../../../../../types/apps/crm/amocrm/AmoCRMUserModel'
import {AmoCRMLineUpdateInitValues, IAmoCRMLineUpdate} from '../../../../../../types/apps/crm/amocrm/AmoCRMLineModel'
import {IAmoCRMPipelineModel} from '../../../../../../types/apps/crm/amocrm/AmoCRMPipelineModel'

type Props = {
  line: IAppAmoCRMLine
}

const amoCRMLineEditShema = Yup.object().shape({
  line: Yup.string().required('Line is required'),
  description: Yup.string(),
  pipeline: Yup.string(),
  responsible_user: Yup.number(),
  smart_call: Yup.boolean(),
  start_time: Yup.string(),
  end_time: Yup.string(),
  incoming_call: Yup.string(),
})

const OneLineAmoCRM: FC<Props> = ({line}) => {
  const {appId} = useParams()
  const {data: app} = appsAPI.useFetchAppByIdQuery(appId!)
  const [lineState, updateLineState] = useState(false)
  const [smartCallState, updateSmartCallState] = useState(line.smart_call)
  const [updateApp, {isLoading}] = appsAPI.useUpdateAppMutation()
  const [allLines, setAllLines] = useState<IAppAmoCRMLine[]>(app?.settings.amocrm.lines!)
  const [users] = useState<IAmoCRMUserModel[]>(app?.settings.amocrm.users!)
  const [pipelines] = useState<IAmoCRMPipelineModel[]>(app?.settings.amocrm.pipelines!)
  const [responsibleUser, setResponsibleUser] = useState<IAmoCRMUserModel | undefined>()

  useEffect(() => {
    if (app && app.settings.amocrm.lines !== undefined) {
      setAllLines(app.settings.amocrm.lines)
    }
  }, [app])

  useEffect(() => {
    if (users.length > 0) {
      const user = users.find((user) => user.amocrm_user_id === line.responsible_user)
      if (user !== undefined) {
        setResponsibleUser(user)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line])

  const formik = useFormik<IAmoCRMLineUpdate>({
    initialValues: Object.assign(AmoCRMLineUpdateInitValues, {
      id: line.id,
      line: line.line === '' ? 'All lines' : line.line,
      description: line.line === '' ? 'All lines' : line.description,
      pipeline: line.pipeline,
      responsible_user: users.length > 0 ? responsibleUser?.amocrm_user_id : line.responsible_user,
      smart_call: line.smart_call,
      start_time: line.start_time.slice(0, 5),
      end_time: line.end_time.slice(0, 5),
      incoming_call: line.incoming_call_mode.none
        ? 'None'
        : line.incoming_call_mode.contact
        ? 'Contact'
        : line.incoming_call_mode.contact_lead
        ? 'ContactLead'
        : 'Unsorted',
    }),
    validationSchema: amoCRMLineEditShema,

    onSubmit: async (values) => {
      const editLine: IAppAmoCRMLine = {
        id: line.id,
        line: values.line === 'All lines' ? '' : values.line,
        description: values.description === 'All lines' ? '' : values.description,
        pipeline: values.pipeline,
        responsible_user:
          values.responsible_user === undefined
            ? users.length > 0
              ? +users[0].amocrm_user_id
              : 0
            : +values.responsible_user,
        smart_call: values.smart_call,
        start_time: values.start_time,
        end_time: values.end_time,
        incoming_call_mode: {
          none: values.incoming_call === 'None' ? true : false,
          contact: values.incoming_call === 'Contact' ? true : false,
          contact_lead: values.incoming_call === 'ContactLead' ? true : false,
          unsorted: values.incoming_call === 'Unsorted' ? true : false,
        },
      }
      const otherLines = allLines.filter((_line) => _line.id !== editLine.id)
      const data = {
        id: appId,
        settings: {
          amocrm: {
            lines: [...otherLines, editLine],
          },
        },
      }
      await updateApp(data as IAppsModel)
      updateLineState(false)
    },
  })

  const deleteLine = () => {
    if (line.line !== 'All lines') {
      const updatedLines = allLines.filter((_line) => _line.id !== line.id)
      const data = {
        id: appId,
        settings: {
          amocrm: {
            lines: updatedLines,
          },
        },
      }
      updateApp(data as IAppsModel)
    }
    updateLineState(false)
  }

  return (
    <>
      {!lineState && (
        <tr role='row' className='text-gray-900 text-center'>
          <td role='cell' className='min-w-150px pe-0'>
            <input
              className='form-control form-control-sm form-control-flush text-center'
              value={line.line === '' ? 'All lines' : line.line}
              readOnly
            />
          </td>
          <td role='cell' className='min-w-140px pe-0 px-0'>
            <input
              className='form-control form-control-sm form-control-flush text-gray-900 text-center p-0'
              value={line.line === '' ? 'All lines' : line.description}
              disabled
              readOnly
            />
          </td>
          <td role='cell' className='min-w-100px pe-0 px-0'>
            <select className='form-control form-control-sm form-control-flush text-center text-gray-900' disabled>
              <option value={line.pipeline}>{line.pipeline}</option>
            </select>
          </td>
          <td role='cell' className='min-w-150px pe-0 px-0'>
            {line.responsible_user !== 0 ? (
              <>
                {responsibleUser?.amocrm_user_id} <br /> {responsibleUser?.fullname}
              </>
            ) : (
              <>{line.responsible_user}</>
            )}
          </td>
          <td role='cell' className='min-w-85px pe-0 px-0'>
            <div className='form-check form-check-custom form-check-solid form-check-sm d-block'>
              <input
                className='form-check-input'
                type='checkbox'
                id={'smCall' + line.line}
                checked={line.smart_call}
                readOnly
                disabled
              />
              <label className='form-check-label' htmlFor={'smCall' + line.line}></label>
            </div>
          </td>
          <td role='cell' className='min-w-150px pe-0 px-0'>
            <div className='d-flex'>
              <input
                className='form-control form-control-sm form-control-flush text-center p-0'
                value={line.start_time.slice(0, 5)}
                readOnly
                disabled
              />
              <label className='align-self-center text-center'>—</label>
              <input
                className='form-control form-control-sm form-control-flush text-center p-0'
                value={line.end_time.slice(0, 5)}
                readOnly
                disabled
              />
            </div>
          </td>
          <td role='cell' className='min-w-100px pe-0 px-0'>
            <select
              className='badge badge-light-primary form-check-input min-w-100px'
              value={
                line.incoming_call_mode.none
                  ? 'None'
                  : line.incoming_call_mode.contact
                  ? 'Contact'
                  : line.incoming_call_mode.contact_lead
                  ? 'ContactLead'
                  : 'Unsorted'
              }
              disabled
            >
              <option value='None'>None</option>
              <option value='Unsorted'>Unsorted</option>
              <option value='Contact'>Contact</option>
              <option value='ContactLead'>Contact + Lead</option>
            </select>
          </td>
          <td role='cell' className='min-w-150px pe-0 px-0'>
            <button
              type='button'
              onClick={() => {
                updateLineState(true)
              }}
              className='btn btn-icon btn-light-primary btn-sm me-1 align-self-center'
            >
              <KTSVG path='/media/icons/duotune/art/art005.svg' className='align-self-center' />
            </button>
          </td>
        </tr>
      )}
      {lineState && (
        <tr role='row' className='text-center'>
          <td className='d-none'>
            <form
              onSubmit={formik.handleSubmit}
              noValidate
              method='GET'
              id={'lineForm' + line?.id}
              className='form'
            ></form>
            <input type='hidden' {...formik.getFieldProps('id')} />
          </td>
          <td role='cell' className='min-w-150px px-0'>
            <input
              className='form-control form-control-sm form-control-flush text-center'
              disabled={line.line === '' ? true : false}
              form={'lineForm' + line?.id}
              {...formik.getFieldProps('line')}
            />
            {formik.touched.line && formik.errors.line && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block text-danger'>{formik.errors.line}</div>
              </div>
            )}
          </td>
          <td role='cell' className='min-w-140px pe-0 px-0'>
            <input
              className='form-control form-control-sm form-control-flush text-center'
              disabled={line.line === '' ? true : false}
              form={'lineForm' + line?.id}
              {...formik.getFieldProps('description')}
            />
          </td>
          <td role='cell' className='min-w-100px pe-0 px-0'>
            <select
              className='form-control form-control-sm form-control-flush text-center text-gray-900'
              {...formik.getFieldProps('pipeline')}
              form={'lineForm' + line?.id}
              value={line.pipeline}
            >
              {pipelines.length > 0 ? (
                pipelines?.map((pipeline: IAmoCRMPipelineModel) => (
                  <option value={pipeline.name} key={pipeline.id}>
                    {pipeline.name}
                  </option>
                ))
              ) : (
                <option value={line.pipeline}>{line.pipeline}</option>
              )}
            </select>
          </td>
          <td role='cell' className='min-w-150px pe-0 px-0'>
            <select
              className='form-control form-control-sm form-control-flush text-center text-gray-900'
              {...formik.getFieldProps('responsible_user')}
              form={'lineForm' + line?.id}
              value={formik.values.responsible_user}
            >
              {users.length > 0 ? (
                users?.map((user: IAmoCRMUserModel) => (
                  <option value={user.amocrm_user_id} key={user.id}>
                    {user.amocrm_user_id} - {user.fullname}
                  </option>
                ))
              ) : (
                <option value={line.responsible_user}>{line.responsible_user}</option>
              )}
            </select>
            {formik.touched.responsible_user && formik.errors.responsible_user && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block text-danger'>{formik.errors.responsible_user}</div>
              </div>
            )}
          </td>
          <td role='cell' className='min-w-85px pe-0 px-0'>
            <div className='form-check form-check-custom form-check-solid form-check-sm d-block text-center'>
              <input
                className='form-check-input'
                type='checkbox'
                id={'editsm' + line.line}
                defaultChecked={line.smart_call}
                form={'lineForm' + line?.id}
                onClick={(e: any) => {
                  updateSmartCallState(e.target.checked)
                }}
                {...formik.getFieldProps('smart_call')}
              />
              <label className='form-check-label' htmlFor={'editsm' + line.line}></label>
            </div>
          </td>
          <td role='cell' className='min-w-150px pe-0 px-0 text-center d-flex'>
            <input
              className={clsx(
                'form-control form-control-sm form-control-flush text-center text-gray-700',
                smartCallState && `text-gray-900`
              )}
              id={'editstartTime' + line.line}
              form={'lineForm' + line?.id}
              disabled={!smartCallState}
              {...formik.getFieldProps('start_time')}
            />
            <label className='align-self-center'>—</label>
            <input
              className={clsx(
                'form-control form-control-sm form-control-flush text-center text-gray-700',
                smartCallState && `text-gray-900`
              )}
              id={'editendTime' + line.line}
              form={'lineForm' + line?.id}
              disabled={!smartCallState}
              {...formik.getFieldProps('end_time')}
            />
          </td>
          <td role='cell' className='min-w-100px pe-0 px-0'>
            <select
              className='badge badge-light-primary form-check-input min-w-100px'
              {...formik.getFieldProps('incoming_call')}
            >
              <option value='None'>None</option>
              <option value='Unsorted'>Unsorted</option>
              <option value='Contact'>Contact</option>
              <option value='ContactLead'>Contact + Lead</option>
            </select>
          </td>
          <td role='cell' className='min-w-150px pe-0 px-0'>
            <button
              type='submit'
              title='Save'
              className='btn btn-icon btn-light-success btn-active-color-light btn-sm me-1 align-self-center'
              form={'lineForm' + line?.id}
              disabled={isLoading}
            >
              {!isLoading && <KTSVG path='/media/icons/duotune/general/gen037.svg' className='align-self-center' />}
              {isLoading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  <span className='spinner-border spinner-border-sm align-middle'></span>
                </span>
              )}
            </button>
            <button
              form={'lineForm' + line?.id}
              className='btn btn-icon btn-light-primary btn-active-color-light btn-sm me-1 align-self-center'
              title='Close'
              onClick={() => {
                updateLineState(false)
                formik.resetForm()
              }}
              disabled={isLoading}
              hidden={isLoading}
            >
              {!isLoading && <KTSVG path='/media/icons/duotune/general/gen034.svg' className='align-self-center' />}
            </button>
            {line.line !== '' && (
              <button
                form={'lineForm' + line?.id}
                title='Delete'
                onClick={() => {
                  deleteLine()
                  formik.resetForm()
                }}
                className='btn btn-icon btn-light-danger btn-active-color-light btn-sm me-1 align-self-center'
                disabled={isLoading}
                hidden={isLoading}
              >
                {!isLoading && <KTSVG path='/media/icons/duotune/general/gen027.svg' className='align-self-center' />}
              </button>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

export {OneLineAmoCRM}
